/**
 * Kundali Constants
 * Common constants used across Kundali chart components
 */

// English Sign Abbreviations
export const SIGN_ABBR = {
  'Aries': 'Ar', 'Taurus': 'Ta', 'Gemini': 'Ge', 'Cancer': 'Ca',
  'Leo': 'Le', 'Virgo': 'Vi', 'Libra': 'Li', 'Scorpio': 'Sc',
  'Sagittarius': 'Sg', 'Capricorn': 'Cp', 'Aquarius': 'Aq', 'Pisces': 'Pi'
};

// Telugu Sign Abbreviations (unique — no collisions)
export const SIGN_ABBR_TE = {
  'Aries': 'మేష', 'Taurus': 'వృష', 'Gemini': 'మిథు', 'Cancer': 'కర్క',
  'Leo': 'సిం', 'Virgo': 'కన్య', 'Libra': 'తుల', 'Scorpio': 'వృశ్చి',
  'Sagittarius': 'ధను', 'Capricorn': 'మకర', 'Aquarius': 'కుంభ', 'Pisces': 'మీన'
};

// Planet Colors for chart rendering
export const PLANET_COLORS = {
  Su: '#FF6B00', Mo: '#C0C0C0', Ma: '#FF0000', Me: '#32CD32',
  Ju: '#FFD700', Ve: '#FF69B4', Sa: '#4169E1', Ra: '#800080', Ke: '#8B4513',
};

// Zodiac Signs in order
export const SIGNS = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

// Planet Abbreviation Translations (Telugu)
export const PLANET_ABBR_TE = {
  'Su': 'సూ', 'Mo': 'చం', 'Ma': 'కు', 'Me': 'బు',
  'Ju': 'గు', 'Ve': 'శు', 'Sa': 'శని', 'Ra': 'రా', 'Ke': 'కే'
};

// Full Planet Names with symbols
export const PLANETS = [
  { name: 'Sun', symbol: '☉', abbr: 'Su', color: '#FF6B00' },
  { name: 'Moon', symbol: '☽', abbr: 'Mo', color: '#C0C0C0' },
  { name: 'Mars', symbol: '♂', abbr: 'Ma', color: '#FF0000' },
  { name: 'Mercury', symbol: '☿', abbr: 'Me', color: '#32CD32' },
  { name: 'Jupiter', symbol: '♃', abbr: 'Ju', color: '#FFD700' },
  { name: 'Venus', symbol: '♀', abbr: 'Ve', color: '#FF69B4' },
  { name: 'Saturn', symbol: '♄', abbr: 'Sa', color: '#4169E1' },
  { name: 'Rahu', symbol: '☊', abbr: 'Ra', color: '#800080' },
  { name: 'Ketu', symbol: '☋', abbr: 'Ke', color: '#8B4513' },
];

// House Lords mapping by sign
export const HOUSE_LORDS = {
  'Aries': 'Mars', 'Taurus': 'Venus', 'Gemini': 'Mercury', 'Cancer': 'Moon',
  'Leo': 'Sun', 'Virgo': 'Mercury', 'Libra': 'Venus', 'Scorpio': 'Mars',
  'Sagittarius': 'Jupiter', 'Capricorn': 'Saturn', 'Aquarius': 'Saturn', 'Pisces': 'Jupiter'
};

// House Significance descriptions
export const HOUSE_SIGNIFICANCE = {
  1: 'Self, Personality, Health',
  2: 'Wealth, Family, Speech',
  3: 'Siblings, Courage, Communication',
  4: 'Mother, Home, Vehicles, Education',
  5: 'Children, Intelligence, Creativity',
  6: 'Enemies, Debts, Health Issues',
  7: 'Marriage, Partnership, Business',
  8: 'Longevity, Obstacles, Occult',
  9: 'Fortune, Father, Religion, Higher Learning',
  10: 'Career, Profession, Status',
  11: 'Gains, Income, Friendships',
  12: 'Losses, Expenses, Spirituality, Foreign Travel'
};

// Planet exaltation signs
export const EXALTATION_SIGNS = {
  'Sun': 'Aries', 'Moon': 'Taurus', 'Mars': 'Capricorn', 'Mercury': 'Virgo',
  'Jupiter': 'Cancer', 'Venus': 'Pisces', 'Saturn': 'Libra'
};

// Planet debilitation signs
export const DEBILITATION_SIGNS = {
  'Sun': 'Libra', 'Moon': 'Scorpio', 'Mars': 'Cancer', 'Mercury': 'Pisces',
  'Jupiter': 'Capricorn', 'Venus': 'Virgo', 'Saturn': 'Aries'
};

// Planet own signs
export const OWN_SIGNS = {
  'Sun': ['Leo'], 'Moon': ['Cancer'], 'Mars': ['Aries', 'Scorpio'],
  'Mercury': ['Gemini', 'Virgo'], 'Jupiter': ['Sagittarius', 'Pisces'],
  'Venus': ['Taurus', 'Libra'], 'Saturn': ['Capricorn', 'Aquarius']
};

// Remedies data for each planet
export const PLANET_REMEDIES = {
  'Sun': { gemstone: 'Ruby (Manik)', mantra: 'Om Suryaya Namaha', color: 'Red/Saffron', day: 'Sunday', duration: '21-40 days' },
  'Moon': { gemstone: 'Pearl (Moti)', mantra: 'Om Chandraya Namaha', color: 'White', day: 'Monday', duration: '27 days' },
  'Mars': { gemstone: 'Red Coral (Moonga)', mantra: 'Om Mangalaya Namaha', color: 'Red', day: 'Tuesday', duration: '21 days' },
  'Mercury': { gemstone: 'Emerald (Panna)', mantra: 'Om Budhaya Namaha', color: 'Green', day: 'Wednesday', duration: '21 days' },
  'Jupiter': { gemstone: 'Yellow Sapphire (Pukhraj)', mantra: 'Om Gurave Namaha', color: 'Yellow', day: 'Thursday', duration: '16-40 days' },
  'Venus': { gemstone: 'Diamond (Heera)', mantra: 'Om Shukraya Namaha', color: 'White/Pink', day: 'Friday', duration: '21 days' },
  'Saturn': { gemstone: 'Blue Sapphire (Neelam)', mantra: 'Om Shanaye Namaha', color: 'Blue/Black', day: 'Saturday', duration: '19-40 days' },
  'Rahu': { gemstone: 'Hessonite (Gomed)', mantra: 'Om Rahave Namaha', color: 'Brown', day: 'Saturday', duration: '18 days' },
  'Ketu': { gemstone: 'Cat\'s Eye (Lehsunia)', mantra: 'Om Ketave Namaha', color: 'Gray', day: 'Tuesday', duration: '7-21 days' }
};

// Benefic and Malefic planets
export const BENEFIC_PLANETS = ['Jupiter', 'Venus', 'Moon', 'Mercury'];
export const MALEFIC_PLANETS = ['Saturn', 'Mars', 'Rahu', 'Ketu'];
export const BENEFIC_HOUSES = [1, 4, 5, 7, 9, 10, 11];

// Day Lords (for weekly planetary periods)
export const DAY_LORDS = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
export const DAY_LORDS_HI = ['Ravi', 'Chandra', 'Mangal', 'Budh', 'Guru', 'Shukra', 'Shani'];
export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const DAY_NAMES_HI = ['Ravivaar', 'Somvaar', 'Mangalvaar', 'Budhvaar', 'Guruvaar', 'Shukravaar', 'Shanivaar'];

// Manglik Dosha houses
export const MANGLIK_HOUSES = [1, 2, 4, 7, 8, 12];

// Layman-friendly tooltip explanations
export const LAYMAN_TIPS = {
  en: {
    lagna: 'Lagna (Ascendant) is the zodiac sign rising on the eastern horizon at your exact birth time & place. It shapes your personality and life path.',
    nakshatra: 'Your birth star (Nakshatra) is determined by the Moon\'s exact position. It reveals your core nature, temperament, and is used for matching horoscopes.',
    pada: 'Each Nakshatra has 4 quarters (Padas). Your pada decides the starting syllable for naming a child.',
    rashi: 'Rashi (Moon Sign) is the zodiac sign where the Moon was at your birth. In South India, this is your main zodiac identity.',
    rashiLord: 'The planet that rules your Moon sign. Its strength and position strongly influence your life.',
    nakshatraLord: 'The planet that rules your birth star. Its Dasha (period) affects major life events.',
    namingSyllable: 'Traditional first syllable for naming based on your Nakshatra & Pada. Many families use this when naming a baby.',
    navamsa: 'Navamsa (D9) is a divisional chart primarily used to assess marriage, spouse, and spiritual growth.',
    lagnaChart: 'The main birth chart (Rashi chart / D1). It maps all 9 planets into 12 houses from your Ascendant.',
    house: 'Houses (Bhavas) are 12 areas of life — self, wealth, siblings, home, children, health, marriage, longevity, fortune, career, gains, and losses.',
    degree: 'The exact position of a planet within its zodiac sign (0° to 30°). More precise = more accurate predictions.',
    retrograde: 'Retrograde (℞) means the planet appears to move backward from Earth\'s view. It often intensifies the planet\'s influence inward.',
    combust: 'Combust (☼) means the planet is very close to the Sun and its energy is "burned" — weakening its positive effects.',
    dignity: 'Dignity shows if a planet is strong (Exalted/Own Sign) or weak (Debilitated) based on which sign it sits in.',
    strength: 'A score (0–100) showing how powerfully a planet can deliver results in your chart.',
    manglik: 'Manglik Dosha occurs when Mars sits in houses 1, 2, 4, 7, 8, or 12. It can affect marriage compatibility. Many cancellation rules apply — don\'t panic!',
    ayanamsa: 'Ayanamsa is the correction angle between Western (Tropical) and Vedic (Sidereal) zodiacs. Lahiri is the standard in India.',
    houseAnalysis: 'Each of the 12 houses governs a different life area. Weak houses may benefit from specific remedies like gemstones, mantras, or colors.',
    remedy: 'Remedies are traditional suggestions — wearing a gemstone, chanting a mantra, or wearing specific colors on specific days — to strengthen a weak planet.',
    panchang: 'Panchang means "five limbs" — the five elements of Hindu time: Tithi (lunar day), Vara (weekday), Nakshatra (Moon\'s star), Yoga (Sun-Moon combination), and Karana (half-tithi). Every auspicious occasion is checked against these.',
    kalamTimings: 'Rahu Kalam, Yamagandam & Gulika Kalam are daily inauspicious periods. South Indians avoid starting important tasks, travel, or auspicious events during these times.',
    luckyInfo: 'Lucky numbers, colors, days & gemstones are derived from your birth Nakshatra lord and Rashi lord. Wearing the correct gemstone on the right finger can strengthen beneficial planets.',
    lifePredictions: 'These are general predictions based on planetary positions in your chart. Marriage timing depends on 7th house, career on 10th house, and health on 6th house & Lagna lord. For detailed predictions, consult an astrologer.',
  },
  te: {
    lagna: 'లగ్నం అంటే మీ జన్మ సమయంలో తూర్పు దిక్కున ఉదయిస్తున్న రాశి. ఇది మీ వ్యక్తిత్వం, జీవన మార్గాన్ని నిర్ణయిస్తుంది.',
    nakshatra: 'జన్మ నక్షత్రం — చంద్రుని స్థానం ద్వారా నిర్ణయించబడుతుంది. ఇది మీ స్వభావం, గుణాలను చెబుతుంది. జాతక మ్యాచింగ్‌కు ఉపయోగిస్తారు.',
    pada: 'ప్రతి నక్షత్రంలో 4 పాదాలు ఉంటాయి. మీ పాదం ఆధారంగా పేరు పెట్టే అక్షరం నిర్ణయిస్తారు.',
    rashi: 'రాశి (చంద్ర రాశి) — మీ జన్మ సమయంలో చంద్రుడు ఉన్న రాశి. దక్షిణ భారతంలో ఇదే మీ ప్రధాన రాశి.',
    rashiLord: 'మీ రాశికి అధిపతి గ్రహం. దీని బలం మీ జీవితాన్ని బాగా ప్రభావితం చేస్తుంది.',
    nakshatraLord: 'మీ నక్షత్రానికి అధిపతి గ్రహం. దీని దశ (కాలం) జీవితంలో ముఖ్యమైన సంఘటనలను ప్రభావితం చేస్తుంది.',
    namingSyllable: 'నక్షత్రం & పాదం ఆధారంగా పేరు పెట్టే సంప్రదాయ అక్షరం. పిల్లలకు పేరు పెట్టేటప్పుడు ఈ అక్షరం వాడతారు.',
    navamsa: 'నవాంశ (D9) చార్ట్ — వివాహం, భాగస్వామి, ఆధ్యాత్మిక ఎదుగుదలను అంచనా వేయడానికి ఉపయోగిస్తారు.',
    lagnaChart: 'ప్రధాన జన్మ కుండలి (రాశి చార్ట్ / D1). మీ లగ్నం నుండి 12 భావాలలో 9 గ్రహాలను చూపిస్తుంది.',
    house: 'భావాలు (12) — ఆత్మ, ధనం, సోదరులు, గృహం, సంతానం, ఆరోగ్యం, వివాహం, ఆయుష్షు, భాగ్యం, వృత్తి, లాభం, నష్టం.',
    degree: 'రాశిలో గ్రహం యొక్క ఖచ్చితమైన స్థానం (0° నుండి 30° వరకు).',
    retrograde: 'వక్రం (℞) — భూమి నుండి చూస్తే గ్రహం వెనక్కి కదులుతున్నట్లు కనిపిస్తుంది. ఇది ప్రభావాన్ని తీవ్రం చేస్తుంది.',
    combust: 'అస్తం (☼) — గ్రహం సూర్యునికి చాలా దగ్గరగా ఉండి "కాలిపోయినట్లు" — మంచి ఫలితాలు బలహీనపడతాయి.',
    dignity: 'గౌరవం — గ్రహం బలంగా (ఉచ్చ/స్వక్షేత్రం) లేదా బలహీనంగా (నీచ) ఉందా అని చూపిస్తుంది.',
    strength: 'గ్రహ బలం (0–100) — మీ కుండలిలో గ్రహం ఎంత శక్తివంతంగా ఫలితాలు ఇవ్వగలదో చూపే స్కోరు.',
    manglik: 'మంగళ దోషం — కుజుడు 1, 2, 4, 7, 8, 12 భావాలలో ఉంటే వస్తుంది. వివాహ అనుకూలతను ప్రభావితం చేయవచ్చు. రద్దు నియమాలు చాలా ఉన్నాయి — భయపడకండి!',
    ayanamsa: 'అయనాంశ — పాశ్చాత్య (సాయన) మరియు వైదిక (నిరయన) రాశి చక్రాల మధ్య తేడా. లాహిరి భారతదేశంలో ప్రామాణికం.',
    houseAnalysis: '12 భావాలు 12 జీవిత రంగాలను పాలిస్తాయి. బలహీనమైన భావాలకు రత్నాలు, మంత్రాలు వంటి ప్రత్యామ్నాయాలు ఉన్నాయి.',
    remedy: 'రత్నం ధరించడం, మంత్రం జపించడం, నిర్దిష్ట రోజున నిర్దిష్ట రంగు ధరించడం — బలహీనమైన గ్రహాన్ని బలపరచడానికి సంప్రదాయ సూచనలు.',
    panchang: 'పంచాంగం అంటే "ఐదు అంగాలు" — తిథి (చంద్ర దినం), వారం, నక్షత్రం, యోగం, కరణం. ప్రతి శుభ సందర్భంలో వీటిని చూస్తారు.',
    kalamTimings: 'రాహు కాలం, యమగండం & గుళిక కాలం రోజువారీ అశుభ సమయాలు. దక్షిణ భారతీయులు ఈ సమయాలలో ముఖ్యమైన పనులు, ప్రయాణం, శుభ కార్యాలు మానుకుంటారు.',
    luckyInfo: 'అదృష్ట సంఖ్యలు, రంగులు, రోజులు & రత్నాలు మీ జన్మ నక్షత్ర అధిపతి, రాశి అధిపతి నుండి తీసుకోబడతాయి. సరైన వేలికి సరైన రత్నం ధరించడం శుభ గ్రహాలను బలపరుస్తుంది.',
    lifePredictions: 'ఇవి మీ జాతకంలోని గ్రహ స్థానాల ఆధారంగా సాధారణ అంచనాలు. వివాహ సమయం 7వ భావం, వృత్తి 10వ భావం, ఆరోగ్యం 6వ భావం & లగ్నాధిపతి మీద ఆధారపడి ఉంటుంది. వివరమైన అంచనాల కోసం జ్యోతిష్కుని సంప్రదించండి.',
  },
};
